"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons";
import {HtmlContent} from "@/shared/ui/data-display/html-content";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import {MoreQuestions, MoreQuestionsProps} from "./parts/more-questions/more-questions";

import "./styles/faq.scss";

export type FaqProps = {
    title: string;
    faqs: Array<{
        id: number | string;
        question: string;
        answer: string;
    }>;
    moreQuestions: MoreQuestionsProps | Array<MoreQuestionsProps>;
    space?: SpaceProps;
    anchorId?: string;
    /** Max items to show initially. If omitted, all items are shown. */
    visibleCount?: number;
    titleFullWidth?: boolean | undefined;
};

export function Faq({title, faqs, moreQuestions, space, anchorId, visibleCount = 5, titleFullWidth = false}: FaqProps): JSX.Element {
    const [openItems, setOpenItems] = useState<Set<number | string>>(new Set());
    const [showAll, setShowAll] = useState(false);

    const {t} = useTranslation("common");
    const hasMore = visibleCount !== undefined && faqs.length > visibleCount;

    const toggleItem = (id: number | string): void => {
        setOpenItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const sectionVars = useSectionSpace(space);

    return (
        <section className="faq" style={sectionVars} id={anchorId}>
            <ContainerBase>
                <Typography
                    as="h2"
                    size={{desktop: 32, mobile: 18}}
                    weight={600}
                    className={titleFullWidth ? "faq__title__no-width" : "faq__title"}
                    lineHeight={120}
                >
                    {title}
                </Typography>
                <div className="faq__wrap">
                    <div className="faq__list">
                        {faqs.map((faq, index) => {
                            const isOpen = openItems.has(faq.id);
                            const isHidden = hasMore && !showAll && visibleCount !== undefined && index >= visibleCount;
                            return (
                                <div
                                    key={faq.id}
                                    className={`faq__item ${isOpen ? "faq__item--open" : ""} ${isHidden ? "faq__item--hidden" : "faq__item--visible"}`}
                                >
                                    <button type="button" className="faq__question-wrapper" onClick={() => toggleItem(faq.id)} aria-expanded={isOpen}>
                                        <Typography as="h3" size={{desktop: 18, mobile: 14}} weight={500} className="faq__question">
                                            {faq.question}
                                        </Typography>
                                        <span className="faq__icon">
                                            <span className="faq__icon-line faq__icon-line--horizontal"></span>
                                            <span className="faq__icon-line faq__icon-line--vertical"></span>
                                        </span>
                                    </button>

                                    <div className="faq__answer-wrapper">
                                        <HtmlContent html={faq.answer} className="faq__answer" />
                                    </div>
                                </div>
                            );
                        })}
                        {hasMore && !showAll && (
                            <ButtonBase variant="secondary-outline" size="sm" width="fit" className="faq__show-more" onClick={() => setShowAll(true)}>
                                {t("showMore")}
                                <SmartImage
                                    src={assetPath("/images/icons/arrow-black-down.svg")}
                                    alt=""
                                    width={12}
                                    height={10}
                                    style={{marginLeft: 8}}
                                />
                            </ButtonBase>
                        )}
                    </div>
                    {moreQuestions && (
                        <div className="faq__more-questions">
                            <MoreQuestions {...(Array.isArray(moreQuestions) ? moreQuestions[0] : moreQuestions)} />
                        </div>
                    )}
                </div>
            </ContainerBase>
        </section>
    );
}
